/**
 * An overlay with an animated icon for loading indication.
 *
 * options:
 *
 *  permutation
 *      affects the loader look and feel, and the loader icon size. default is Loader.PERMUTATION.A
 *
 *  el
 *      the target element for the loader. it must have relative or absolute positioning.
 *
 *  zIndex
 *      the desired stacking order. if no z-order is passed, the loader will compute the currently highest z-index.
 *
 *  isLEWrapRequired
 *      determines if the loader markup will be wrapped with an element with the 'le' classname.
 *
 *  text
 *      (optional) a text to be presented above the icon.
 *
 *  button
 *      (optional) an object in the form {text: 'string', callback: function () {}, tooltip: 'optional string'}. the loader will render a
 *      button ui-component below the icon with the passed text, and will assign the passed callback as a handler
 *      to its onClick event.
 */
define(function (require) {
    "use strict";

    var Marionette = require("marionette");
    var WidgetUtils = require('ui/WidgetUtils');
    var template = require("ui/loader/templates/loader.tmpl");
    var templateWithLE = require("ui/loader/templates/loaderWithLE.tmpl");
    var Button = require("ui/button/Button");
    var Bubble = require("ui/bubble/Bubble");
    var _ = require("underscore");
    var $ = require("jquery");

    var Loader = Marionette.ItemView.extend({

        version: "1.2",
        el: "#contentWrapper",
        withUIComponents: true,

        initialize : function (options) {
            this.templateId = (new Date()).getTime().toString();
            this.permutation = options.permutation || Loader.PERMUTATION.A;
            this.el = options.el;
            this.zIndex = options.zIndex;
            this.isLEWrapRequired = options.isLEWrapRequired;
            this.template = this._getTemplate();
            this.text = options.text;
            this.isShowAnimation = _.has(options, "isShowAnimation") ? options.isShowAnimation : true;
            this.button = options.button;
        },

        render: function () {

            if (!this.zIndex){
                this.zIndex = WidgetUtils.highestZIndex(this.$el) + 1;
            }
            this.$el.append(this.template());
            this.$el.find(".loader").addClass(this.templateId);
            this.$el.find(".loader").css("z-index", this.zIndex);
            this.$el.find(".overlay").addClass("overlay-perm-" + this.permutation);
            this.$el.find(".iconLoader").addClass("icon-perm-" + this.permutation);

            if (this._isTextValid()) {
                this._createText();
            }
            if(!this.isShowAnimation) {
                this.hideAnimationOnly();
            }

            if (this._isButtonValid()) {
                this._createButton();
                this._bindButtonEvent();
            }

            return this;
        },

        close: function () {
            this._destroyButton();
            if (this.isLEWrapRequired) {
                this.$el.find(".le .loader." + this.templateId.toString()).parent().remove();
            } else {
                this.$el.find(".loader." + this.templateId.toString()).remove();
            }
            return this;
        },

        hideAnimationOnly: function () {
            this.$el.find(".iconLoader").hide();
        },

        _isTextValid: function () {
            return this.text &&
                typeof this.text === "string";
        },

        _isButtonValid: function () {
            return this.button &&
                typeof this.button === "object" &&
                this.button.text &&
                this.button.callback &&
                typeof this.button.text === "string" &&
                typeof this.button.callback === "function";
        },

        _getTemplate: function () {
            if (this.isLEWrapRequired) {
                return templateWithLE;
            }
            else {
                return template;
            }
        },

        _createText: function () {
            this.$el.find(".loader").append(
                $("<div>")
                    .text(this.text)
                    .addClass("text")
                    .addClass("text-perm-" + this.permutation)
            );
        },

        _createButton: function () {
            var buttonEl = $("<div>")
                .addClass("button")
                .addClass("button-perm-" + this.permutation);
            this.$el.find(".loader").append(buttonEl);
            this.buttonComponent = new Button({
                el: buttonEl,
                text: this.button.text,
                permutation: Button.PERMUTATION.BTN,
                onClick: this.button.callback
            });
            this.buttonComponent.render();
        },

        _bindButtonEvent: function(){
            if (!this.button.tooltip) {
                return;
            }
            if (this.buttonComponent.$el.children().length > 0){
                this.buttonControl = this.buttonComponent.$el.children(0);
                $(this.buttonControl).mouseenter(_.bind(function(){
                    this._createButtonBubble();
                }, this));
                $(this.buttonControl).mouseleave(_.bind(function(){
                    this._hideBubble();
                }, this));
            }
        },

        _createButtonBubble: function(){
            if (this.buttonControl){
                this.bubbleComponent = new Bubble({
                    targetControl: this.buttonControl,
                    text: this.button.tooltip,
                    showCloseButton: false,
                    zIndex: this.zIndex + 1
                });
                this.bubbleComponent.render();
            }
        },

        _destroyButton: function () {
            if (this.buttonComponent) {
                this._hideBubble();
                this.buttonComponent.close();
                delete this.buttonComponent;
            }
        },

        _hideBubble: function(){
            if (this.bubbleComponent){
                this.bubbleComponent.close();
                delete this.bubbleComponent;
            }
        }
    });

    Loader.PERMUTATION = {
        A   :1,
        B   :2,
        C   :3,
        D   :4,
        F   :5,
        G   :6,
        H   :7,
        I   :8,
        J   :11, // for CB notification
        K   :10, // for CB Loader
        L   :9,   // for CB KWL Loader - gray overlay,
        M   :12 // for personalization
    };

    return Loader;
});
